import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Phone, ExternalLink } from 'lucide-react';
import TopBar from '../components/TopBar';
import Header from '../components/Header';
import Footer from '../components/Footer';

const STATIC_HEADER = { cta_url: '/donate' };
const STATIC_FOOTER = { contact_email: 'info@securethevotemd.com' };

const contacts = [
  ['Allegany County', '301-777-5931', 'http://alleganygov.org/158/election-office'],
  ['Anne Arundel County', '410-222-6600', 'https://www.aacounty.org/boards-and-commissions/board-of-elections/'],
  ['Baltimore City', '410-396-5550', 'https://boe.baltimorecity.gov'],
  ['Baltimore County', '410-887-5700', 'http://www.baltimorecountymd.gov/agencies/elections/index.html'],
  ['Calvert County', '410-535-2214', 'https://www.calvertcountymd.gov/154/2022-election'],
  ['Caroline County', '410-479-8145', 'https://www.carolinemd.org/713/Board-of-Elections'],
  ['Carroll County', '410-386-2080', 'https://elections.carrollcountymd.gov'],
  ['Cecil County', '410-996-5310', 'https://www.cecilcountymd.gov/170/Board-of-Elections'],
  ['Charles County', '301-934-8972', 'https://www.charlescountymd.gov/government/board-of-elections'],
  ['Dorchester County', '410-228-2560', 'https://www.docomdelections.org/'],
  ['Frederick County', '301-600-8683', 'https://www.frederickcountymd.gov/elections'],
  ['Garrett County', '301-334-6985', 'https://www.garrettcountymd.gov/elections'],
  ['Harford County', '410-638-3565', 'https://www.harfordvotes.gov'],
  ['Howard County', '410-313-5820', 'https://www.howardcountymd.gov/board-elections'],
  ['Kent County', '410-778-0038', 'https://www.kentcountyelections.org'],
  ['Montgomery County', '240-777-8500', 'https://mcg.montgomerycountymd.gov/elections/index2.html'],
  ["Prince George's County", '301-341-7300', 'https://www.princegeorgescountymd.gov/559/board-of-elections'],
  ["Queen Anne's County", '410-758-0832', 'https://www.qacelections.com/'],
  ["St. Mary's County", '301-475-4200 ext 71614', 'https://www.stmarysmd.com/supervisorofelections/'],
  ['Somerset County', '410-651-0767', 'https://www.somersetmd.us/government/board_of_elections.php'],
  ['Talbot County', '410-770-8099', 'http://www.talbotcountymd.gov/index.php?page=Election_Board'],
  ['Washington County', '240-313-2050', 'http://www.washco-mdelections.org/'],
  ['Wicomico County', '410-548-4830', 'http://www.wicomicocounty.org'],
  ['Worcester County', '410-632-1320', 'http://www.co.worcester.md.us/departments/board-elections'],
];

const phoneHref = (phone) => `tel:${phone.replace(/ext/i, ',').replace(/[^0-9,]/g, '')}`;

const BoardOfElectionsDirectoryPage = () => {
  useEffect(() => {
    const title = 'Board of Elections Directory';
    const description = 'Maryland Board of Elections Directory with county election office links and clickable phone numbers.';
    document.title = title;

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);

    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', title);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="boe-directory-page">
      <TopBar />
      <Header config={STATIC_HEADER} />

      <div className="relative pt-[50px]" style={{ backgroundColor: '#9E1B32' }}>
        <div className="py-16 sm:py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl text-center">
            <motion.h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Board of Elections Directory
            </motion.h1>
            <motion.div
              className="w-16 h-1 bg-[#F6BF58] mx-auto mt-6"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            />
          </div>
        </div>
      </div>

      <main className="py-12 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <div className="mb-8 text-center">
            <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto">
              Use this directory to contact your local Maryland Board of Elections office. County names link to the official election office website, and phone numbers are clickable on supported devices.
            </p>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg">
            <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr] bg-[#9E1B32] px-5 py-4 text-sm font-bold uppercase tracking-wide text-white">
              <div>County / Jurisdiction</div>
              <div className="hidden sm:block">Phone</div>
            </div>
            <div className="divide-y divide-slate-100">
              {contacts.map(([county, phone, link]) => (
                <div key={county} className="grid grid-cols-1 gap-3 px-5 py-4 transition-colors hover:bg-slate-50 sm:grid-cols-[1.4fr_1fr] sm:items-center">
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 font-semibold text-slate-900 hover:text-[#9E1B32]"
                  >
                    {county}
                    <ExternalLink size={15} className="text-[#9E1B32]" />
                  </a>
                  <a
                    href={phoneHref(phone)}
                    className="inline-flex items-center gap-2 font-medium text-[#9E1B32] hover:text-[#6B1029]"
                    aria-label={`Call ${county} Board of Elections at ${phone}`}
                  >
                    <Phone size={16} />
                    {phone}
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer config={STATIC_FOOTER} />
    </div>
  );
};

export default BoardOfElectionsDirectoryPage;
